import React, { useState, useEffect } from 'react';
import { User } from '@/types';
import { Notification, NotificationFilters } from '@/types/notifications';
import { notificationsApi } from '@/services/notificationsApi';
import styles from './W_NotificatonsTab.module.css';
import M_NotificationItem from '@/components/M_Molecules/M_NotificationItem/M_NotificationItem';
import M_SelectPicker from '@/components/M_Molecules/M_SelectPicker/M_SelectPicker';
import W_NotificationEmptyContainer from '../../W_NotificationEmptyContainer/W_NotificationEmptyContainer';
import A_Notification from '@/components/A_Atoms/A_Notification/A_Notification';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';

interface W_NotificatonsTabProps {
  user: User;
}

export const W_NotificatonsTab: React.FC<W_NotificatonsTabProps> = ({
  user,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('unread');
  const [showReinviteToast, setShowReinviteToast] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    const filters: NotificationFilters = {};
    if (filter === 'unread') {
      filters.isRead = false;
    } else if (filter === 'read') {
      filters.isRead = true;
    }
    notificationsApi.getNotifications(filters).then(res => {
      if (res.success && res.data) {
        setNotifications(res.data.notifications ?? []);
      }
    });
  }, [filter, user.id]);

  const handleFilterChange = (option: string) => {
    const unread = getTranslation(language, 'profileNotifications.unread');
    const read = getTranslation(language, 'profileNotifications.read');
    if (option === unread) {
      setFilter('unread');
    } else if (option === read) {
      setFilter('read');
    } else {
      setFilter('all');
    }
  };

  const handleRead = (notificationId: string) => {
    notificationsApi.markAsRead(notificationId).then(res => {
      if (res.success) {
        if (filter === 'unread') {
          setNotifications(prev => prev.filter(n => n.id !== notificationId));
        } else {
          setNotifications(prev =>
            prev.map(n =>
              n.id === notificationId ? { ...n, isRead: true } : n
            )
          );
        }
      }
    });
  };

  const handleAccept = (notificationId: string) => {
    notificationsApi.acceptInvite(notificationId).then(res => {
      if (res.success) {
        notificationsApi.markAsRead(notificationId).catch(() => {});
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    });
  };

  const handleDecline = (notificationId: string) => {
    notificationsApi.declineInvite(notificationId).then(res => {
      if (res.success) {
        notificationsApi.markAsRead(notificationId).catch(() => {});
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    });
  };

  const handleReinvite = (notificationId: string) => {
    notificationsApi.reinviteCoauthor(notificationId).then(res => {
      if (res.success) {
        setShowReinviteToast(true);
      }
    });
  };

  const handleCancel = (notificationId: string) => {
    notificationsApi.deleteNotification(notificationId).then(res => {
      if (res.success) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    });
  };

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.title}>
        {getTranslation(language, 'profileNotifications.title')}
      </h4>
      <M_SelectPicker
        options={[
          getTranslation(language, 'profileNotifications.unread'),
          getTranslation(language, 'profileNotifications.read'),
          getTranslation(language, 'profileNotifications.all'),
        ]}
        selectedOption={
          filter === 'unread'
            ? getTranslation(language, 'profileNotifications.unread')
            : filter === 'read'
              ? getTranslation(language, 'profileNotifications.read')
              : getTranslation(language, 'profileNotifications.all')
        }
        onSelect={handleFilterChange}
      />
      <div className={styles.notifications_list}>
        {notifications.map(notification => (
          <M_NotificationItem
            key={notification.id}
            notification={notification}
            onAccept={() => handleAccept(notification.id)}
            onDecline={() => handleDecline(notification.id)}
            onReinvite={() => handleReinvite(notification.id)}
            onRead={() => handleRead(notification.id)}
            onCancel={() => handleCancel(notification.id)}
          />
        ))}
        {notifications.length === 0 && (
          <W_NotificationEmptyContainer filterType={filter} />
        )}
      </div>
      <A_Notification
        type='success'
        message={getTranslation(
          language,
          'profileNotifications.reinviteSuccess'
        )}
        isVisible={showReinviteToast}
        onClose={() => setShowReinviteToast(false)}
        duration={3000}
      />
    </div>
  );
};
